import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { RichTextEditor } from './RichTextEditor';
import { toast } from 'sonner';
import { Paperclip, X, FileText, Image as ImageIcon } from 'lucide-react';
import { fileSize } from '../AnnouncementAttachments';

const EMPTY = { block_permits: false, title: '', html: '', mode: 'once', status_ids: [], location_ids: [], start_date: '', end_date: '', is_active: true };

export const Pick = ({ label, options, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label>{label} <span className="text-xs font-normal text-slate-400">(boş = hepsi)</span></Label>
    <div className="grid sm:grid-cols-3 gap-2 p-3 border rounded-lg bg-slate-50 max-h-32 overflow-y-auto" data-testid={testId}>
      {options.map(o => (
        <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={value.includes(o.id)} onCheckedChange={(v) => onChange(v ? [...value, o.id] : value.filter(x => x !== o.id))} data-testid={`${testId}-${o.id}`} />{o.name}
        </label>
      ))}
    </div>
  </div>
);

export const UserAnnouncementFormDialog = ({ open, item, statuses, locations, onClose, onSave }) => {
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [removed, setRemoved] = useState([]);
  useEffect(() => { if (open) { setF({ ...EMPTY, ...(item || {}), start_date: item?.start_date || '', end_date: item?.end_date || '' }); setFiles([]); setRemoved([]); } }, [open, item]);
  const existing = (item?.attachments || []).filter(a => !removed.includes(a.id));
  const addFiles = (list) => {
    const ok = [];
    for (const file of list) {
      if (!/\.(pdf|jpe?g|png|webp)$/i.test(file.name)) { toast.error(`${file.name}: sadece PDF, JPG, PNG veya WEBP`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: en fazla 10 MB olabilir`); continue; }
      ok.push(file);
    }
    const room = 5 - existing.length - files.length;
    if (ok.length > room) toast.error('Bir duyuruya en fazla 5 dosya eklenebilir');
    setFiles(prev => [...prev, ...ok.slice(0, Math.max(room, 0))]);
  };
  const set = (p) => setF(prev => ({ ...prev, ...p }));
  const empty = !f.html.replace(/<[^>]*>/g, '').trim() || !f.title.trim();
  const { id, counts, created_at, created_by, updated_at, attachments, ...body } = f;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ua-form-dialog">
        <DialogHeader><DialogTitle>{item?.id ? 'Duyuruyu Düzenle' : 'Yeni Kullanıcı Duyurusu'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Gösterim türü</Label>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="ua-mode">
              {[['once', 'Tek gösterimlik', 'Firma bir kez görür, "X" ile kapatır.'], ['ack', 'Okudum, anladım onaylı', '"Okudum ve anladım" onayı verilmeden pencere kapanmaz.']].map(([k, l, d]) => (
                <button key={k} type="button" onClick={() => set({ mode: k })} data-testid={`ua-mode-${k}`}
                  className={`rounded-lg border-2 p-3 text-left transition-colors ${f.mode === k ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <p className="text-sm font-semibold text-slate-900">{l}</p><p className="text-xs text-slate-500">{d}</p>
                </button>
              ))}
            </div>
          </div>
          {f.mode === 'ack' && (
            <label className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm cursor-pointer" data-testid="ua-block-permits">
              <Checkbox checked={f.block_permits} onCheckedChange={(v) => set({ block_permits: !!v })} className="mt-0.5" data-testid="ua-block-permits-check" />
              <span><b>Onaylanana kadar yeni talep engellensin</b><span className="block text-xs text-slate-600">Firma bu duyuruyu onaylamadan yeni talep açamaz, talep kopyalayamaz ve taslağı incelemeye gönderemez.</span></span>
            </label>
          )}
          <div className="space-y-1.5"><Label>Başlık *</Label><Input value={f.title} onChange={(e) => set({ title: e.target.value })} data-testid="ua-title-input" /></div>
          <div className="space-y-1.5"><Label>Metin *</Label>{open && <RichTextEditor key={item?.id || 'new'} value={f.html} onChange={(html) => set({ html })} />}</div>
          <div className="space-y-1.5" data-testid="ua-attachments">
            <Label>Ekler <span className="text-xs font-normal text-slate-400">(PDF, JPG, PNG, WEBP · en fazla 10 MB · en fazla 5 dosya)</span></Label>
            <div className="space-y-1.5">
              {existing.map(a => (
                <div key={a.id} className="flex items-center gap-2 rounded-md border bg-slate-50 px-3 py-1.5 text-sm" data-testid={`ua-att-${a.id}`}>
                  {a.content_type.startsWith('image/') ? <ImageIcon className="h-4 w-4 text-sky-600" /> : <FileText className="h-4 w-4 text-rose-600" />}
                  <span className="flex-1 truncate">{a.name}</span><span className="text-xs text-slate-400">{fileSize(a.size)}</span>
                  <button type="button" onClick={() => setRemoved(r => [...r, a.id])} className="text-slate-400 hover:text-rose-600" data-testid={`ua-att-remove-${a.id}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
              {files.map((file, i) => (
                <div key={`${file.name}-${i}`} className="flex items-center gap-2 rounded-md border border-dashed border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm" data-testid={`ua-newfile-${i}`}>
                  <Paperclip className="h-4 w-4 text-emerald-600" /><span className="flex-1 truncate">{file.name}</span><span className="text-xs text-slate-400">{fileSize(file.size)} · kaydedince yüklenecek</span>
                  <button type="button" onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
                </div>
              ))}
              {existing.length + files.length < 5 && (
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm text-slate-600 transition-colors hover:border-slate-400 hover:bg-slate-50" data-testid="ua-file-drop">
                  <Paperclip className="h-4 w-4" />Dosya seç
                  <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { addFiles([...e.target.files]); e.target.value = ''; }} data-testid="ua-file-input" />
                </label>
              )}
            </div>
          </div>
          <Pick label="Firma statüsü" options={statuses} value={f.status_ids} onChange={(v) => set({ status_ids: v })} testId="ua-statuses" />
          <Pick label="Lokasyon" options={locations} value={f.location_ids} onChange={(v) => set({ location_ids: v })} testId="ua-locations" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Yayın başlangıcı <span className="text-xs font-normal text-slate-400">(boş = hemen)</span></Label><Input type="date" value={f.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="ua-start-date" /></div>
            <div className="space-y-1.5"><Label>Yayın bitişi <span className="text-xs font-normal text-slate-400">(boş = süresiz)</span></Label><Input type="date" value={f.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="ua-end-date" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_active} onCheckedChange={(v) => set({ is_active: v })} data-testid="ua-active-switch" />Aktif</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ua-cancel-btn">Vazgeç</Button>
          <Button disabled={empty} onClick={() => onSave({ ...body, block_permits: f.mode === 'ack' && f.block_permits, start_date: f.start_date || null, end_date: f.end_date || null }, files, removed)} data-testid="ua-save-btn">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
